import { formatFileSize } from "@mg/schemas/src/commons/file-limits";
import { UploadSimple, X } from "@phosphor-icons/react";
import { Text } from "@radix-ui/themes";
import {
  type DragEvent,
  type KeyboardEvent,
  type ReactNode,
  useCallback,
  useId,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

export interface FileDropzoneProps {
  /** Called with the validated, accepted files whenever a selection is made. */
  onFiles: (files: File[]) => void;
  /** Native `accept` string, e.g. ".pdf,.png" or "image/*". Omit to accept all. */
  accept?: string;
  /** Allow selecting more than one file. */
  multiple?: boolean;
  /** Reject any file larger than this (bytes) with an inline message. */
  maxSizeBytes?: number;
  /** Disable all interaction. */
  disabled?: boolean;
  /** Helper copy shown under the prompt and wired up as `aria-describedby`. */
  helperText?: ReactNode;
  /** Primary prompt copy. */
  label?: string;
  /** Accessible name for the drop region (defaults to the label). */
  ariaLabel?: string;
  /** Render the list of selected file names with remove / clear controls. */
  showFileList?: boolean;
  /** Called when the selection is cleared (single remove or clear-all). */
  onClear?: () => void;
  /** testid for the wrapping element. */
  "data-testid"?: string;
  /** testid for the real (visually hidden) `<input type="file">`. */
  inputTestId?: string;
  className?: string;
  /** Forwarded to the hidden input's `name` (e.g. for native form posts). */
  name?: string;
}

/** Split a native `accept` string into individual patterns. */
function parseAccept(accept?: string): string[] {
  if (accept == null || accept.trim() === "") return [];
  return accept
    .split(",")
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
}

/** Returns true when `file` satisfies at least one of the accept `patterns`. */
export function fileMatchesAccept(file: File, patterns: string[]): boolean {
  if (patterns.length === 0) return true;
  const name = file.name.toLowerCase();
  const type = file.type.toLowerCase();
  return patterns.some((pattern) => {
    const pat = pattern.toLowerCase();
    if (pat === "*" || pat === "*/*" || pat === ".*") return true;
    if (pat.startsWith(".")) return name.endsWith(pat);
    if (pat.endsWith("/*")) return type.startsWith(pat.slice(0, -1));
    return type === pat;
  });
}

/** Returns true when the drag carries external OS files (not an internal DOM drag). */
function isExternalFileDrag(event: DragEvent): boolean {
  return Array.from(event.dataTransfer?.types ?? []).includes("Files");
}

/**
 * Unified, accessible drag-and-drop file picker.
 *
 * Renders a labelled drop region that opens the OS file picker on click or
 * keyboard (Enter / Space), accepts drag-and-drop with a visible hover state,
 * validates against `accept` / `maxSizeBytes`, and lists the selected files
 * with remove / clear controls. A real (visually hidden) `<input type="file">`
 * is kept in the DOM so the OS picker works and Playwright `setInputFiles`
 * keeps targeting it via `inputTestId`.
 */
export function FileDropzone({
  onFiles,
  accept,
  multiple = false,
  maxSizeBytes,
  disabled = false,
  helperText,
  label = "Drag and drop files here, or click to browse",
  ariaLabel,
  showFileList = true,
  onClear,
  "data-testid": dataTestId = "file-dropzone",
  inputTestId = "file-dropzone-input",
  className,
  name,
}: FileDropzoneProps) {
  const { t } = useTranslation("ui");
  const inputRef = useRef<HTMLInputElement>(null);
  const dragDepth = useRef(0);
  const [isDragOver, setIsDragOver] = useState(false);
  const [selected, setSelected] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);

  const helperId = useId();
  const errorId = useId();
  const acceptPatterns = parseAccept(accept);

  const validate = useCallback(
    (incoming: File[]): File[] => {
      const accepted: File[] = [];
      let rejection: string | null = null;

      for (const file of incoming) {
        if (!fileMatchesAccept(file, acceptPatterns)) {
          rejection ??= `${file.name} is not an accepted file type.`;
          continue;
        }
        if (maxSizeBytes != null && file.size > maxSizeBytes) {
          rejection ??= `${file.name} exceeds the ${formatFileSize(
            maxSizeBytes,
          )} limit.`;
          continue;
        }
        accepted.push(file);
      }

      setError(rejection);
      return accepted;
    },
    [acceptPatterns, maxSizeBytes],
  );

  const commit = useCallback(
    (incoming: File[]) => {
      if (disabled || incoming.length === 0) return;
      const accepted = validate(incoming);
      if (accepted.length === 0) return;
      const next = multiple ? accepted : accepted.slice(0, 1);
      setSelected(next);
      onFiles(next);
    },
    [disabled, validate, multiple, onFiles],
  );

  const openPicker = useCallback(() => {
    if (disabled) return;
    inputRef.current?.click();
  }, [disabled]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        openPicker();
      }
    },
    [openPicker],
  );

  const handleDragEnter = useCallback(
    (event: DragEvent) => {
      if (disabled || !isExternalFileDrag(event)) return;
      event.preventDefault();
      dragDepth.current += 1;
      if (dragDepth.current === 1) setIsDragOver(true);
    },
    [disabled],
  );

  const handleDragOver = useCallback(
    (event: DragEvent) => {
      if (disabled || !isExternalFileDrag(event)) return;
      event.preventDefault();
    },
    [disabled],
  );

  const handleDragLeave = useCallback((event: DragEvent) => {
    if (!isExternalFileDrag(event)) return;
    event.preventDefault();
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (event: DragEvent) => {
      if (disabled || !isExternalFileDrag(event)) return;
      event.preventDefault();
      event.stopPropagation();
      dragDepth.current = 0;
      setIsDragOver(false);
      commit(Array.from(event.dataTransfer?.files ?? []));
    },
    [disabled, commit],
  );

  const clearAll = useCallback(() => {
    setSelected([]);
    setError(null);
    if (inputRef.current) inputRef.current.value = "";
    onClear?.();
  }, [onClear]);

  const removeAt = useCallback(
    (index: number) => {
      setSelected((prev) => {
        const next = prev.filter((_, i) => i !== index);
        if (next.length === 0) {
          if (inputRef.current) inputRef.current.value = "";
          onClear?.();
        } else {
          onFiles(next);
        }
        return next;
      });
    },
    [onFiles, onClear],
  );

  const describedBy =
    [helperText != null ? helperId : null, error != null ? errorId : null]
      .filter(Boolean)
      .join(" ") || undefined;

  return (
    <div className={className} data-testid={dataTestId}>
      <input
        ref={inputRef}
        type="file"
        name={name}
        accept={accept}
        multiple={multiple}
        disabled={disabled}
        className="hidden"
        data-testid={inputTestId}
        aria-hidden="true"
        tabIndex={-1}
        onChange={(event) => {
          const files = Array.from(event.target.files ?? []);
          commit(files);
          // Allow re-selecting the same file (onChange won't fire otherwise).
          event.target.value = "";
        }}
      />

      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-label={ariaLabel ?? label}
        aria-disabled={disabled}
        aria-describedby={describedBy}
        data-testid={`${dataTestId}-zone`}
        onClick={openPicker}
        onKeyDown={handleKeyDown}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={[
          "flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-6 py-8 text-center outline-none transition-colors motion-reduce:transition-none",
          "focus-visible:ring-2 focus-visible:ring-puntt-accent-8",
          disabled
            ? "cursor-not-allowed border-puntt-neutral-gray-6 bg-puntt-neutral-gray-2 opacity-60"
            : isDragOver
              ? "cursor-pointer border-puntt-accent-8 bg-puntt-accent-2"
              : "cursor-pointer border-puntt-neutral-gray-6 bg-puntt-neutral-gray-1 hover:border-puntt-accent-7 hover:bg-puntt-accent-2/40",
        ].join(" ")}
      >
        <div className="flex size-10 items-center justify-center rounded-full bg-puntt-neutral-gray-3">
          <UploadSimple
            size={20}
            aria-hidden="true"
            className={
              isDragOver ? "text-puntt-accent-9" : "text-puntt-neutral-gray-10"
            }
          />
        </div>
        <Text size="2" weight="medium" className="text-puntt-neutral-gray-12">
          {isDragOver ? "Drop files to upload" : label}
        </Text>
        {helperText != null && (
          <Text
            id={helperId}
            size="1"
            className="text-puntt-neutral-gray-10"
            data-testid={`${dataTestId}-helper`}
          >
            {helperText}
          </Text>
        )}
      </div>

      {error != null && (
        <Text
          id={errorId}
          role="alert"
          size="1"
          color="red"
          className="mt-2 block"
          data-testid={`${dataTestId}-error`}
        >
          {error}
        </Text>
      )}

      {showFileList && selected.length > 0 && (
        <ul
          className="mt-2 flex flex-col gap-1"
          data-testid={`${dataTestId}-file-list`}
        >
          {selected.map((file, index) => (
            <li
              key={`${file.name}-${index}`}
              className="flex items-center justify-between gap-2 rounded-md bg-puntt-neutral-gray-2 px-3 py-2"
              data-testid={`${dataTestId}-file-${index}`}
            >
              <Text
                size="1"
                className="truncate text-puntt-neutral-gray-12"
                title={file.name}
              >
                {file.name}
              </Text>
              <button
                type="button"
                aria-label={`Remove ${file.name}`}
                data-testid={`${dataTestId}-remove-${index}`}
                onClick={() => removeAt(index)}
                disabled={disabled}
                className="shrink-0 rounded p-0.5 text-puntt-neutral-gray-9 transition-colors hover:text-puntt-neutral-gray-12 disabled:opacity-50 motion-reduce:transition-none"
              >
                <X size={14} aria-hidden="true" />
              </button>
            </li>
          ))}
          {multiple && selected.length > 1 && (
            <li>
              <button
                type="button"
                onClick={clearAll}
                disabled={disabled}
                data-testid={`${dataTestId}-clear`}
                className="text-xs text-puntt-accent-9 hover:underline disabled:opacity-50"
              >
                {t("clearAll")}
              </button>
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

export default FileDropzone;
